import { useSyncExternalStore } from 'react';

/** Light/dark is independent of the accent palette. */
export type Mode = 'light' | 'dark';
export type Accent = 'momentum' | 'neutral' | 'blue' | 'emerald' | 'violet';

/** The resolved `data-color` value written to <html> (one per accent × mode). */
export type ColorTheme =
  | 'premium' | 'neutral' | 'blue' | 'emerald' | 'violet' | 'dark'
  | 'neutral-dark' | 'blue-dark' | 'emerald-dark' | 'violet-dark';

export interface ThemeConfig {
  antPrimary: string;
  antPrimaryText: string;
  chart: [string, string, string, string];
}

export const ACCENTS: Accent[] = ['momentum', 'neutral', 'blue', 'emerald', 'violet'];

/** accent × mode → the `data-color` block defined in index.css */
const DATA_COLOR: Record<Accent, Record<Mode, ColorTheme>> = {
  momentum: { light: 'premium', dark: 'dark' },
  neutral:  { light: 'neutral', dark: 'neutral-dark' },
  blue:     { light: 'blue',    dark: 'blue-dark' },
  emerald:  { light: 'emerald', dark: 'emerald-dark' },
  violet:   { light: 'violet',  dark: 'violet-dark' },
};

/** AntD ConfigProvider primary + chart ramp per accent per mode. */
const ACCENT_CONFIG: Record<Accent, Record<Mode, ThemeConfig>> = {
  momentum: {
    light: { antPrimary: '#F05409', antPrimaryText: '#ffffff', chart: ['#F05409', '#FF7A33', '#FFB088', '#16A34A'] },
    dark:  { antPrimary: '#F05409', antPrimaryText: '#ffffff', chart: ['#F05409', '#FF7A33', '#FFB088', '#34D399'] },
  },
  neutral: {
    light: { antPrimary: '#7FAFD8', antPrimaryText: '#ffffff', chart: ['#7FAFD8', '#AFCBE8', '#F59E0B', '#10B981'] },
    dark:  { antPrimary: '#7FAFD8', antPrimaryText: '#ffffff', chart: ['#7FAFD8', '#AFCBE8', '#F59E0B', '#34D399'] },
  },
  blue: {
    light: { antPrimary: '#7FAFD8', antPrimaryText: '#ffffff', chart: ['#7FAFD8', '#AFCBE8', '#F59E0B', '#10B981'] },
    dark:  { antPrimary: '#7FAFD8', antPrimaryText: '#ffffff', chart: ['#7FAFD8', '#AFCBE8', '#F59E0B', '#34D399'] },
  },
  emerald: {
    light: { antPrimary: '#059669', antPrimaryText: '#ffffff', chart: ['#059669', '#34D399', '#F59E0B', '#3B82F6'] },
    dark:  { antPrimary: '#059669', antPrimaryText: '#ffffff', chart: ['#10B981', '#34D399', '#F59E0B', '#60A5FA'] },
  },
  violet: {
    light: { antPrimary: '#7C3AED', antPrimaryText: '#ffffff', chart: ['#7C3AED', '#A78BFA', '#F59E0B', '#10B981'] },
    dark:  { antPrimary: '#7C3AED', antPrimaryText: '#ffffff', chart: ['#8B5CF6', '#A78BFA', '#F59E0B', '#34D399'] },
  },
};

const STORAGE_MODE = 'fair-fest-mode';
const STORAGE_ACCENT = 'fair-fest-accent';
const STORAGE_LEGACY = 'fair-fest-color';

/** Migrate the old single-value theme into {mode, accent}. */
const LEGACY_MAP: Record<string, { mode: Mode; accent: Accent }> = {
  premium: { mode: 'light', accent: 'momentum' },
  dark:    { mode: 'dark',  accent: 'momentum' },
  neutral: { mode: 'light', accent: 'neutral' },
  blue:    { mode: 'light', accent: 'blue' },
  emerald: { mode: 'light', accent: 'emerald' },
  violet:  { mode: 'light', accent: 'violet' },
};

interface ThemeState { mode: Mode; accent: Accent; }

const readInitial = (): ThemeState => {
  try {
    const storedMode = localStorage.getItem(STORAGE_MODE);
    const storedAccent = localStorage.getItem(STORAGE_ACCENT) as Accent | null;
    if ((storedMode === 'light' || storedMode === 'dark') && storedAccent && ACCENTS.includes(storedAccent)) {
      return { mode: storedMode, accent: storedAccent };
    }
    const legacy = localStorage.getItem(STORAGE_LEGACY);
    if (legacy && LEGACY_MAP[legacy]) return LEGACY_MAP[legacy];
  } catch { /* localStorage unavailable */ }
  return { mode: 'dark', accent: 'momentum' }; // default: Momentum dark
};

/* ─── Shared store ───────────────────────────────────────────────────────────
   A single module-level state so every useTheme() consumer — including the root
   <ConfigProvider> in App.tsx — reacts to theme changes instantly. Without this,
   each component held its own useState copy and AntD's algorithm/primary went
   stale on theme switches (dark modals, etc.) until a full reload.            */
let state: ThemeState = readInitial();
const listeners = new Set<() => void>();

const applyToDOM = () => {
  const root = document.documentElement;
  root.setAttribute('data-color', DATA_COLOR[state.accent][state.mode]);
  root.setAttribute('data-mode', state.mode);
};

// Sync <html> to the stored/default theme as soon as the module loads.
applyToDOM();

const update = (partial: Partial<ThemeState>) => {
  const next = { ...state, ...partial };
  if (next.mode === state.mode && next.accent === state.accent) return;
  state = next;
  applyToDOM();
  try {
    localStorage.setItem(STORAGE_MODE, state.mode);
    localStorage.setItem(STORAGE_ACCENT, state.accent);
  } catch { /* ignore */ }
  listeners.forEach((l) => l());
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};
const getSnapshot = () => state;

export const setMode = (mode: Mode) => update({ mode });
export const setAccent = (accent: Accent) => update({ accent });

export const useTheme = () => {
  const { mode, accent } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const colorTheme = DATA_COLOR[accent][mode];
  const isDark = mode === 'dark';

  /** Backward-compat: accept a `data-color` value and derive {mode, accent}. */
  const setColorTheme = (ct: ColorTheme) => {
    for (const a of ACCENTS) {
      for (const m of ['light', 'dark'] as Mode[]) {
        if (DATA_COLOR[a][m] === ct) {
          update({ mode: m, accent: a });
          return;
        }
      }
    }
  };

  return {
    mode,
    setMode,
    accent,
    setAccent,
    colorTheme,
    setColorTheme,
    isDark,
    config: ACCENT_CONFIG[accent][mode],
  };
};
